import React, { Fragment } from 'react';

// Components

import SummaryItem from '../SummaryItem';

// Utils

import { DATE_FORMAT } from '@utils/dates/dateMethods';
import { getWomanOfReproductiveAge } from '@utils/householdMember/getWomanOfReproductiveAge';
import { toUTCAndFormat } from '@utils/dates/date.utils';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';

// Constants

import { NO_PRIOR_DATA } from '@constants/ssap/miscellaneous/miscellaneous';
import { PREFERENCES_FOR_PREGNANT } from '@constants/ssap/hhmTypes/dropdownOptions';


const PregnancyInfo = props => {
    
    const {
        currentlyPregnant,
        currentlyPregnantAndEnrolled,
        dueDateOfPregnancy,
        endDateOfPregnancy,
        evaluateForMedicaidIndicator,
        exchangeAndProgramNames: {
            exchangeChip,
            exchangeMedicaid,
            exchangeName,
            exchangeMedicaidOrChip,
            exchangeMedicaidSlashChip
        },
        idx,
        isState,
        member,
        numberBabiesExpectedInPregnancy,
        pregnantIndicator,
        serverDate,
        startDateOfPregnancy,
        stateCode
    } = props;

    const isMedicaidEvaluationQuestionShowed = (currentlyPregnantAndEnrolled ?? []).includes(
        member.applicantGuid
    );
    
    if(!getWomanOfReproductiveAge(member, serverDate)) {
        return null;
    }

    const getDate = (date, isPrevious = false) => {
        if(date === null && isPrevious) {
            return NO_PRIOR_DATA;
        }

        return toUTCAndFormat(date, DATE_FORMAT);
    };

    const getMedicaidEvaluation = (value = true) => {
        // value defaults to true since evaluateForMedicaidIndicator defaults to true.
        // Summary uses the same option labels as the dropdown (state overrides in PREFERENCES_FOR_PREGNANT).
        const match = PREFERENCES_FOR_PREGNANT(stateCode).find(pref => pref.value === value);

        return match ? match.text : !value;
    };

    const personName = normalizeNameOfPerson(member.name);

    const getPregnancyInformation = () => {
        switch(true) {
            case !pregnantIndicator:
                return (
                    <SummaryItem
                        textCode = 'common.is_pregnant'
                        value = { currentlyPregnant }
                        noPriorDataValues = { [ null ] }
                        desc = {
                            [
                                [
                                    `taxHousehold.householdMember[${ idx }].specialCircumstances.currentlyPregnant`
                                ]
                            ]
                        }
                    />
                );

            case pregnantIndicator && !currentlyPregnant:
                return (
                    <Fragment>
                        <SummaryItem
                            textCode = 'common.is_pregnant'
                            value = { currentlyPregnant }
                            noPriorDataValues = { [ null ] }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].specialCircumstances.currentlyPregnant`
                                    ]
                                ]
                            }
                        />
                        {
                            isState.IL && startDateOfPregnancy &&
                            <SummaryItem
                                textCode='common.pregnancy_confirmation_date'
                                value={getDate(startDateOfPregnancy)}
                                ignoreIntl
                                desc={
                                    [
                                        [
                                            `taxHousehold.householdMember[${idx}].specialCircumstances.startDateOfPregnancy`,
                                            getDate,
                                            [true]
                                        ]
                                    ]
                                }
                            />
                        }
                        {
                            endDateOfPregnancy &&
                            <SummaryItem
                                textCode = 'common.pregnancy_end_date'
                                value = { getDate(endDateOfPregnancy) }
                                ignoreIntl
                                desc = {
                                    [
                                        [
                                            `taxHousehold.householdMember[${ idx }].specialCircumstances.endDateOfPregnancy`,
                                            getDate,
                                            [ true ]
                                        ]
                                    ]
                                }
                            />
                        }
                    </Fragment>
                );

            case pregnantIndicator && currentlyPregnant:
                return (
                    <Fragment>
                        <SummaryItem
                            textCode = 'common.is_pregnant'
                            value = { currentlyPregnant }
                            noPriorDataValues = { [ null ] }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].specialCircumstances.currentlyPregnant`
                                    ]
                                ]
                            }
                        />
                        {
                            isState.IL && startDateOfPregnancy &&
                            <SummaryItem
                                textCode='common.pregnancy_confirmation_date'
                                value={getDate(startDateOfPregnancy)}
                                ignoreIntl
                                desc={
                                    [
                                        [
                                            `taxHousehold.householdMember[${idx}].specialCircumstances.startDateOfPregnancy`,
                                            getDate,
                                            [true]
                                        ]
                                    ]
                                }
                            />
                        }
                        <SummaryItem
                            textCode = 'common.expected_number_of_babies'
                            value = { numberBabiesExpectedInPregnancy }
                            noPriorDataValues = { [ 0, null ] }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].specialCircumstances.numberBabiesExpectedInPregnancy`
                                    ]
                                ]
                            }
                        />
                        <SummaryItem
                            textCode = 'common.pregnancy_due_date'
                            value = { getDate(dueDateOfPregnancy) }
                            desc = {
                                [
                                    [
                                        `taxHousehold.householdMember[${ idx }].specialCircumstances.dueDateOfPregnancy`,
                                        getDate,
                                        [ true ]
                                    ]
                                ]
                            }
                        />
                        {isMedicaidEvaluationQuestionShowed && (
                            <SummaryItem
                                textCode = 'ssap.input.legend.tax_credit_preference'
                                value = { getMedicaidEvaluation(evaluateForMedicaidIndicator) }
                                replacements = { {
                                    personName,
                                    exchangeChip,
                                    exchangeMedicaid,
                                    exchangeMedicaidOrChip,
                                    exchangeMedicaidSlashChip,
                                    exchangeName,
                                    br: <br />
                                } }
                                desc = {
                                    [
                                        [
                                            `taxHousehold.householdMember[${ idx }].evaluateForMedicaidIndicator`,
                                            getMedicaidEvaluation
                                        ]
                                    ]
                                }
                            />
                        )}
                    </Fragment>
                );
            default:
                return null;
        }
    };
    
    return (
        <Fragment>
            { getPregnancyInformation() }
        </Fragment>
    );
};

export default PregnancyInfo;